import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Save, Loader2, UserCog, Search, Upload } from "lucide-react";
import { isEmbedded } from "@/lib/tenant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bulk employee editor.
 * URL: /employees-bulk
 *
 * Shows every employee with editable NAME + CODE inline.
 * Rename code cascades across shifts, punches, vault, face baselines, etc.
 * (via /api/timeclock/employees/{old}/change-code)
 */
export default function EmployeeBulkEditPage() {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showCsv, setShowCsv] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeclock/employees/all`);
      setRows(res.data.map((e) => ({
        ...e,
        _origName: e.name,
        _origCode: e.code,
        _newName: e.name,
        _newCode: e.code,
      })));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (unlocked) load(); }, [unlocked]);

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#0F1013] flex items-center justify-center p-4">
        <Card className="bg-white/5 border-white/10 p-8 w-full max-w-sm text-white text-center">
          <UserCog className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <h1 className="font-black text-xl mb-1">Employee Bulk Edit</h1>
          <p className="text-white/60 text-xs mb-4">Master PIN required</p>
          <Input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (pin === "83528" || pin === "4817" || pin === "1111") && setUnlocked(true)}
            data-testid="bulk-edit-pin"
            className="bg-white/10 border-white/20 text-white text-center text-2xl tracking-widest font-mono"
          />
          <Button
            onClick={() => (pin === "83528" || pin === "4817" || pin === "1111") ? setUnlocked(true) : toast.error("Invalid PIN")}
            className="mt-4 w-full bg-amber-400 text-black hover:bg-amber-500"
            data-testid="bulk-edit-unlock"
          >
            Unlock
          </Button>
        </Card>
      </div>
    );
  }

  const setField = (code, field, value) => {
    setRows((r) => r.map((row) => row._origCode === code ? { ...row, [field]: value } : row));
  };

  const saveOne = async (row) => {
    const nameChanged = row._newName !== row._origName;
    const codeChanged = row._newCode !== row._origCode;
    if (!nameChanged && !codeChanged) return { unchanged: true };

    // 1) rename code first (cascades to all collections) — must be before name change
    if (codeChanged) {
      await axios.post(`${API}/timeclock/employees/${row._origCode}/change-code`, {
        new_code: row._newCode,
      });
    }
    // 2) update name (uses whatever code is now active)
    const codeForUpdate = codeChanged ? row._newCode : row._origCode;
    if (nameChanged) {
      await axios.put(`${API}/timeclock/employees/${codeForUpdate}`, {
        name: row._newName,
        pay_type: row.pay_type || "hourly",
        hourly_rate: row.hourly_rate || 0,
        weekly_salary: row.weekly_salary || 0,
        department: row.department || "Other",
        ot_eligible: row.ot_eligible !== false,
        pay_basis: row.pay_basis || "punches",
        pay_category: row.pay_category || "payroll",
        pay_status: row.pay_status || "active",
        is_manager: row.is_manager || false,
      });
    }
    return { ok: true };
  };

  const saveAll = async () => {
    const changed = rows.filter((r) => r._newName !== r._origName || r._newCode !== r._origCode);
    if (!changed.length) return toast.info("Nothing changed");
    setLoading(true);
    let saved = 0, failed = 0;
    for (const r of changed) {
      try { await saveOne(r); saved += 1; }
      catch (e) {
        failed += 1;
        toast.error(`${r._origName}: ${e.response?.data?.detail || "failed"}`);
      }
    }
    toast.success(`Saved ${saved} · Failed ${failed}`);
    setLoading(false);
    load();
  };

  const filtered = rows.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return r._newName.toLowerCase().includes(q) || r._newCode.includes(q) || (r.department || "").toLowerCase().includes(q);
  });
  const changedCount = rows.filter((r) => r._newName !== r._origName || r._newCode !== r._origCode).length;



  return (
    <div className="min-h-screen bg-[#0F1013] text-white">
      <header className="sticky top-0 z-20 bg-black/60 backdrop-blur border-b border-white/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        {!isEmbedded() && (
          <Link to="/timecard" className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        )}
        <h1 className="font-black text-lg flex items-center gap-2">
          <UserCog className="w-5 h-5 text-amber-300" /> Employee Bulk Edit
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-white/40" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search…"
              data-testid="bulk-edit-search"
              className="bg-white/10 border border-white/20 rounded px-8 py-1.5 text-sm text-white placeholder:text-white/40"
            />
          </div>
          <Button onClick={() => setShowCsv(true)} variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="bulk-csv-open">
            <Upload className="w-4 h-4 mr-1" /> Paste CSV
          </Button>
          <Button onClick={saveAll} disabled={loading || !changedCount}
            className="bg-amber-400 text-black hover:bg-amber-500 disabled:opacity-40"
            data-testid="bulk-edit-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save ({changedCount})</>}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4">
        <p className="text-white/60 text-sm mb-3">
          Edit any employee's name (fix nicknames → real names) or reassign a punch code (paste in their old-system code
          for seamless transfer). Changes cascade across schedule, punches, vault, face baselines, and payroll history.
        </p>

        <Card className="bg-white/5 border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/10 text-white/70 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-32">Code</th>
                <th className="text-left px-3 py-2">Department</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const nameChanged = r._newName !== r._origName;
                const codeChanged = r._newCode !== r._origCode;
                return (
                  <tr key={r._origCode} className={`border-t border-white/5 ${nameChanged || codeChanged ? "bg-amber-500/5" : ""}`}>
                    <td className="px-3 py-1.5">
                      <input
                        value={r._newName}
                        onChange={(e) => setField(r._origCode, "_newName", e.target.value)}
                        className="w-full bg-transparent border border-white/10 focus:border-amber-400/50 rounded px-2 py-1 text-white"
                        data-testid={`edit-name-${r._origCode}`}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        value={r._newCode}
                        onChange={(e) => setField(r._origCode, "_newCode", e.target.value.replace(/\D/g, ""))}
                        className="w-full bg-transparent border border-white/10 focus:border-amber-400/50 rounded px-2 py-1 text-white font-mono tabular-nums"
                        data-testid={`edit-code-${r._origCode}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-white/70">{r.department || "—"}</td>
                    <td className="px-3 py-1.5 text-xs">
                      {r.active ? (
                        <span className="text-emerald-300">active</span>
                      ) : (
                        <span className="text-white/40">inactive</span>
                      )}
                      {r.pay_category === "cash" && <span className="ml-2 text-amber-300">cash</span>}
                      {(nameChanged || codeChanged) && <span className="ml-2 text-amber-400">● unsaved</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <p className="text-white/40 text-xs mt-4">
          Tip: click Save at the top to commit all pending changes at once. Codes cascade across every collection —
          schedules, punches, payroll, face baselines all update automatically.
        </p>
      </main>

      {showCsv && (
        <CsvPasteModal
          rows={rows}
          onClose={() => setShowCsv(false)}
          onApply={(matches) => {
            setRows((r) => r.map((row) => {
              const m = matches.find((x) => x.target_code === row._origCode);
              return m ? { ...row, _newName: m.new_name || row._newName, _newCode: m.new_code || row._newCode } : row;
            }));
            setShowCsv(false);
            toast.success(`Staged ${matches.length} changes — click Save to commit`);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CSV paste modal — accepts "name,code" pairs and matches by fuzzy name
// ═══════════════════════════════════════════════════════════════

function CsvPasteModal({ rows, onClose, onApply }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);

  const parse = () => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    // Skip a header row if it looks like one
    const start = /name|code/i.test(lines[0] || "") ? 1 : 0;
    const parsedRows = [];
    for (let i = start; i < lines.length; i++) {
      const line = lines[i];
      // Support: name,code | code,name | "Last, First",code | name<space>code
      let cells = line.includes(",") ? line.split(",").map((s) => s.trim().replace(/^"|"$/g, "")) : line.split(/\s+/);
      // Heuristic: whichever cell is all digits (4-6 chars) is the code
      let name = "", code = "";
      for (const c of cells) {
        if (/^\d{3,6}$/.test(c) && !code) code = c;
        else if (!name) name = c;
        else name += " " + c;
      }
      if (!name || !code) continue;
      parsedRows.push({ raw: line, name, code });
    }

    // Fuzzy match each parsed row to an existing employee
    const matches = parsedRows.map((p) => {
      const emp = findBestMatch(p.name, rows);
      return {
        source_name: p.name,
        source_code: p.code,
        target_code: emp?._origCode || null,
        target_name: emp?._origName || null,
        new_code: p.code,
        new_name: p.name,
        matched: !!emp,
      };
    });
    setPreview({ parsed: parsedRows.length, matches });
  };

  const apply = () => onApply((preview?.matches || []).filter((m) => m.matched));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <Card className="bg-[#1A1620] border-white/10 p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <Upload className="w-6 h-6 text-amber-300" />
          <div>
            <h2 className="text-lg font-black text-white">Paste CSV — Name & Old-System Code</h2>
            <p className="text-white/60 text-xs">One employee per line. Order doesn't matter. Headers auto-skipped.</p>
          </div>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="csv-textarea"
          placeholder={"Juned,7862\nMota,8022\nSalman,7861\n… or …\n7862,Juned Patel\n7861,Salman Iqbal"}
          className="w-full h-48 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm font-mono placeholder:text-white/30"
        />

        <div className="flex gap-2 mt-3">
          <Button onClick={parse} className="bg-white/10 text-white hover:bg-white/20" data-testid="csv-parse">
            Parse & Preview
          </Button>
          <Button onClick={onClose} variant="outline" className="ml-auto border-white/20 text-white hover:bg-white/10">
            Cancel
          </Button>
        </div>

        {preview && (
          <div className="mt-4">
            <div className="text-white/70 text-sm mb-2">
              Parsed {preview.parsed} rows · Matched {preview.matches.filter((m) => m.matched).length}
              {preview.matches.some((m) => !m.matched) && (
                <span className="text-red-300"> · {preview.matches.filter((m) => !m.matched).length} unmatched</span>
              )}
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/10 text-white/70">
                  <tr>
                    <th className="text-left px-2 py-1.5">CSV Name</th>
                    <th className="text-left px-2 py-1.5">CSV Code</th>
                    <th className="text-left px-2 py-1.5">→ Existing Employee</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.matches.map((m, i) => (
                    <tr key={i} className={`border-t border-white/5 ${m.matched ? "" : "bg-red-500/10"}`}>
                      <td className="px-2 py-1 text-white">{m.source_name}</td>
                      <td className="px-2 py-1 font-mono text-amber-300">{m.source_code}</td>
                      <td className="px-2 py-1 text-white/70">
                        {m.matched ? `${m.target_name} (#${m.target_code})` : <span className="text-red-300">— no match —</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={apply} className="mt-3 w-full bg-amber-400 text-black hover:bg-amber-500" data-testid="csv-apply">
              Stage {preview.matches.filter((m) => m.matched).length} Changes (click Save at top to commit)
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Fuzzy match a name against the existing roster. Simple case-insensitive substring + Jaro-ish. */
function findBestMatch(name, rows) {
  const n = name.toLowerCase().trim();
  if (!n) return null;
  // 1. Exact
  let hit = rows.find((r) => r._origName.toLowerCase() === n);
  if (hit) return hit;
  // 2. First-word match
  const firstWord = n.split(/\s+/)[0];
  hit = rows.find((r) => r._origName.toLowerCase().startsWith(firstWord));
  if (hit) return hit;
  // 3. Substring anywhere
  hit = rows.find((r) => r._origName.toLowerCase().includes(firstWord) || firstWord.includes(r._origName.toLowerCase().split(/\s+/)[0]));
  return hit || null;
}
